import type { EpcScheduleTaskRow } from '../../types/scheduleTask';

export type EpcWbsNodeKind = 'stage' | 'task' | 'subtask';

/**
 * Tree node derived from a flat WBS list.
 * - `stage`: root summary (outline level 1, or any summary at top).
 * - `task`: deeper summary (any summary that isn't a stage).
 * - `subtask`: leaf row (non-summary) or any row at max depth.
 */
export type EpcWbsNode = {
  rowIndex: number;
  kind: EpcWbsNodeKind;
  level: number;
  name: string;
  task: EpcScheduleTaskRow;
  children: EpcWbsNode[];
};

function numLevel(raw: unknown): number {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function isSummary(t: EpcScheduleTaskRow): boolean {
  const s = t.isSummary;
  return s === true || s === 'true' || s === 1 || s === '1';
}

/**
 * Build a tree from flat WBS rows. Each row's `outlineLevel` and `isSummary` drives parenting.
 * Stage = top-level summary (lowest outlineLevel or a true root summary).
 */
export function buildWbsTree(tasks: EpcScheduleTaskRow[]): EpcWbsNode[] {
  const roots: EpcWbsNode[] = [];
  const stack: EpcWbsNode[] = [];

  for (let i = 0; i < tasks.length; i += 1) {
    const t = tasks[i]!;
    const L = numLevel(t.outlineLevel);
    while (stack.length > 0 && stack[stack.length - 1]!.level >= L) stack.pop();

    const parent = stack[stack.length - 1];
    const kind: EpcWbsNodeKind = parent == null ? 'stage' : isSummary(t) ? 'task' : 'subtask';

    const node: EpcWbsNode = {
      rowIndex: i,
      kind,
      level: L,
      name: String(t.name ?? '').trim() || `Task ${i + 1}`,
      task: t,
      children: [],
    };
    if (parent) parent.children.push(node);
    else roots.push(node);
    stack.push(node);
  }

  return roots;
}

/** Flatten the tree back into WBS row indices in DFS order (original order). */
export function flattenWbsTree(nodes: EpcWbsNode[]): number[] {
  const out: number[] = [];
  const walk = (n: EpcWbsNode) => {
    out.push(n.rowIndex);
    for (const c of n.children) walk(c);
  };
  for (const n of nodes) walk(n);
  return out;
}

/** Count stages, tasks, and leaf subtasks in the tree. Useful for summary counters. */
export function countWbsKinds(nodes: EpcWbsNode[]): { stages: number; tasks: number; subtasks: number } {
  let stages = 0;
  let tasks = 0;
  let subtasks = 0;
  const walk = (n: EpcWbsNode) => {
    if (n.kind === 'stage') stages += 1;
    else if (n.kind === 'task') tasks += 1;
    else subtasks += 1;
    for (const c of n.children) walk(c);
  };
  for (const n of nodes) walk(n);
  return { stages, tasks, subtasks };
}

/** All descendant row indices (inclusive) of a node, WBS order. */
export function nodeSubtreeIndices(node: EpcWbsNode): number[] {
  const out: number[] = [];
  const walk = (n: EpcWbsNode) => {
    out.push(n.rowIndex);
    for (const c of n.children) walk(c);
  };
  walk(node);
  return out;
}
